import { computed, inject, ref } from 'vue';

import { useDict } from '@/composables/fischerPlugin';
import Validators, { type ValidatorFn, type ValidatorsResult } from '@/utils/validators';

type AllowedCharactersType = {
  [K in keyof ValidatorsResult]: K extends `${infer T}AllowedCharacters` ? T : never;
}[keyof ValidatorsResult];

export type Value = unknown;

export const useValidators = (fieldsOverride?: Record<string, unknown>) => {
  const zipRegEx = ref(null);
  const dict = useDict();
  const injectedFields = inject('fields', null);

  // If the caller provides a flat fields object (e.g. sitecoreData from a prop),
  // normalize PascalCase keys to camelCase and wrap values in { value } to match
  // the injected-fields shape, then merge on top of any injected fields.
  const fields = fieldsOverride
    ? {
        ...(injectedFields ?? {}),
        ...Object.fromEntries(
          Object.entries(fieldsOverride)
            .filter((e): e is [string, string] => typeof e[1] === 'string')
            .map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), { value: v }]),
        ),
      }
    : injectedFields;

  const hasInvalidCharacters = ref(false);

  const rules = computed(() => Validators(dict, fields));

  const validateCharacters = (input: any, type: AllowedCharactersType) => {
    const inputValidator = (rules.value[`${type}AllowedCharacters`][0] as ValidatorFn)(input);

    hasInvalidCharacters.value = inputValidator !== true && input.length;

    return inputValidator;
  };

  const dynamicRule = (defaultRule: any, isDefaultRuleEnabled: boolean) => {
    let rule = defaultRule;
    if (!isDefaultRuleEnabled) {
      rule = [];
    }
    return rule;
  };

  const setCountryZipValidation = (country: string, countryList: any) => {
    let zipValidation = null;
    const elements = countryList?.Elements || countryList?.fields?.Elements;
    if (elements) {
      elements.forEach((element: any) => {
        const elementValue = element.Value || element.fields?.Value?.value;
        if (elementValue === country) {
          zipValidation =
            element['Zip_Code_Validation'] || element.fields?.['Zip Code Validation']?.value;
        }
      });
    }
    zipRegEx.value = zipValidation;
  };

  const validateZip = (value: string, errorMessage?: string) => {
    if (zipRegEx.value && zipRegEx.value !== '' && value && value !== '') {
      const pattern = new RegExp(zipRegEx.value);
      return [
        pattern.test(value) ||
          errorMessage ||
          dict.get('online-Shop.Global-Labels.Error-Message-Zipcode'),
      ];
    }
    return [
      (value && value !== '') || dict.get('online-Shop.Global-Labels.Error-Message-Required'),
    ];
  };

  // Validation for fischertechnik and fischer website
  const userIdentifierRule = (isFite: boolean, input: any, isValidationRuleEnabled: boolean) => {
    let userRules = [];

    if (isFite && (rules.value.isNumber[0] as ValidatorFn)(input)) {
      userRules = isValidationRuleEnabled ? rules.value.fanclubNumber : [];
    } else {
      userRules = isValidationRuleEnabled || hasInvalidCharacters.value ? rules.value.email : [];
    }
    return userRules;
  };

  return {
    rules,
    hasInvalidCharacters,
    validateZip,
    validateCharacters,
    dynamicRule,
    setCountryZipValidation,
    userIdentifierRule,
  };
};
